/**
 * The entries inside a comma-joined value.
 *
 * Exported because the same joined string is edited in three places — the
 * form's multi-select, a grid cell and the Sammelbearbeitung — and a second spelling of the
 * rule is a second answer to "is 'A,B' one drafter or two".
 *
 * A KNOWN entry wins over the comma inside it. The Planart thesaurus contains
 * "Übersicht der Flächen, Schnitte und Hauptprofile" — split on punctuation
 * alone, picking that one option hands back three. So the vocabulary is matched
 * first, longest option first (its own prefix is another option), and only what
 * no option claims is split on the comma.
 *
 * A match must end the text or be followed by a comma: "Befundplan Ost" is one
 * free-text entry, not "Befundplan" plus a stray word.
 */
export function joinedToChips(
    value: unknown,
    /** The offered entries — the only ones allowed to contain a comma. */
    options: readonly string[] = []
): string[] {
    const known = [...options]
        .filter(Boolean)
        .sort(
            (firstOption, secondOption) =>
                secondOption.length - firstOption.length
        );
    const chips: string[] = [];
    let rest = String(value ?? '').trim();

    while (rest) {
        const match = known.find(
            (option) => rest === option || rest.startsWith(`${option},`)
        );
        if (match) {
            chips.push(match);
            rest = rest.slice(match.length).replace(/^,\s*/, '');
            continue;
        }
        const comma = rest.indexOf(',');
        if (comma < 0) {
            chips.push(rest.trim());
            break;
        }
        chips.push(rest.slice(0, comma).trim());
        rest = rest.slice(comma + 1).trimStart();
    }
    return chips.filter(Boolean);
}

/** …and back into the one string the field stores. */
export function chipsToJoined(chips: readonly string[]): string {
    return chips.join(', ');
}
